<script setup lang="ts">
import { Link, router, usePage } from '@inertiajs/vue3';
import {
    FileOutput,
    FilePlus,
    Layers,
    LogIn,
    Maximize2,
    Menu,
    PencilLine,
    ShieldCheck,
} from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import AppLogo from '@/components/AppLogo.vue';
import Breadcrumbs from '@/components/Breadcrumbs.vue';
import ThemeToggle from '@/components/ThemeToggle.vue';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuList,
    NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from '@/components/ui/sheet';
import UserMenuContent from '@/components/UserMenuContent.vue';
import { useCurrentUrl } from '@/composables/useCurrentUrl';
import { getInitials } from '@/composables/useInitials';
import { useSite } from '@/composables/useSite';
import { useTranslations } from '@/composables/useTranslations';
import type { ImageMegaMenuItem } from '@/data/imageHeaderNav';
import {
    bulkImageNavItems as bulkImageNavItemsBase,
    editorImageNavItem as editorImageNavItemBase,
    isImageNavSectionActive,
    optimizeImageNavItems as optimizeImageNavItemsBase,
    transformImageNavItems as transformImageNavItemsBase,
} from '@/data/imageHeaderNav';
import type { PdfMegaMenuItem } from '@/data/pdfHeaderNav';
import {
    convertFromPdfNavItems as convertFromPdfNavItemsBase,
    convertToPdfNavItems as convertToPdfNavItemsBase,
    editPdfNavItems as editPdfNavItemsBase,
    isPdfNavSectionActive,
    organizePdfNavItems as organizePdfNavItemsBase,
    securityPdfNavItems as securityPdfNavItemsBase,
} from '@/data/pdfHeaderNav';
import { localizeHref } from '@/lib/localizeHref';
import { dashboard, login } from '@/routes';
import type { BreadcrumbItem } from '@/types';

type Props = {
    breadcrumbs?: BreadcrumbItem[];
};

const props = withDefaults(defineProps<Props>(), {
    breadcrumbs: () => [],
});

const page = usePage();
const auth = computed(() => page.props.auth);
const loginRegisterEnabled = computed(
    () => page.props.loginRegisterEnabled !== false,
);
const { site } = useSite();
const { t, locale } = useTranslations();
const { currentUrl, isCurrentUrl, whenCurrentUrl } = useCurrentUrl();

function localizeMenuItems<T extends { href: string }>(items: T[]): T[] {
    return items.map((item) => ({
        ...item,
        href: localizeHref(item.href, locale.value),
    }));
}

const convertToPdfItems = computed(() =>
    localizeMenuItems(convertToPdfNavItemsBase),
);
const convertFromPdfItems = computed(() =>
    localizeMenuItems(convertFromPdfNavItemsBase),
);
const organizePdfItems = computed(() =>
    localizeMenuItems(organizePdfNavItemsBase),
);
const editPdfItems = computed(() => localizeMenuItems(editPdfNavItemsBase));
const securityPdfItems = computed(() =>
    localizeMenuItems(securityPdfNavItemsBase),
);
const editorImageNavItem = computed(
    () => localizeMenuItems([editorImageNavItemBase])[0],
);
const transformImageNavItems = computed(() =>
    localizeMenuItems(transformImageNavItemsBase),
);
const optimizeImageNavItems = computed(() =>
    localizeMenuItems(optimizeImageNavItemsBase),
);
const bulkImageNavItems = computed(() =>
    localizeMenuItems(bulkImageNavItemsBase),
);

function isPdfSectionActive(items: PdfMegaMenuItem[]): boolean {
    return isPdfNavSectionActive(currentUrl.value, items);
}

function isImageSectionActive(items: ImageMegaMenuItem[]): boolean {
    return isImageNavSectionActive(currentUrl.value, items);
}

const activeItemStyles =
    'text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100';

const homeAriaLabel = computed(() => t('tools.nav.home', 'Home'));

const mobileNavOpen = ref(false);

let removeMobileNavFinishListener: (() => void) | undefined;

onMounted(() => {
    removeMobileNavFinishListener = router.on('finish', () => {
        mobileNavOpen.value = false;
    });
});

onUnmounted(() => {
    removeMobileNavFinishListener?.();
});
</script>

<template>
    <div>
        <div class="border-b border-sidebar-border/80">
            <div class="mx-auto flex h-16 items-center px-4 md:max-w-7xl">
                <!-- Mobile Menu -->
                <div class="lg:hidden">
                    <Sheet v-model:open="mobileNavOpen">
                        <SheetTrigger :as-child="true">
                            <Button
                                variant="ghost"
                                size="icon"
                                class="mr-2 h-9 w-9"
                                :aria-label="
                                    t(
                                        'tools.nav.open_menu',
                                        'Open navigation menu',
                                    )
                                "
                            >
                                <Menu class="h-5 w-5" aria-hidden="true" />
                            </Button>
                        </SheetTrigger>
                        <SheetContent
                            side="left"
                            class="flex h-full max-h-[100dvh] w-[min(100vw,20rem)] max-w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-sm"
                        >
                            <SheetTitle class="sr-only"
                                >Navigation menu</SheetTitle
                            >
                            <div
                                class="flex shrink-0 items-center border-b border-sidebar-border/80 px-4 py-4 pr-14"
                            >
                                <SheetHeader
                                    class="flex justify-start gap-0 p-0 text-left"
                                >
                                    <Link
                                        :href="dashboard.url()"
                                        prefetch
                                        class="inline-flex max-w-[min(100%,15rem)] rounded-md ring-offset-background outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                        :aria-label="homeAriaLabel"
                                    >
                                        <AppLogo size="sm" />
                                    </Link>
                                </SheetHeader>
                            </div>
                            <div
                                class="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain px-2 py-3 [-webkit-overflow-scrolling:touch]"
                            >
                                <nav
                                    v-if="site === 'pdf'"
                                    class="space-y-1 pb-6"
                                >
                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.to_pdf',
                                                'Convert to PDF',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in convertToPdfItems"
                                        :key="item.href"
                                        :href="item.href"
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.pdf.${item.key}.nav_title`,
                                                t(
                                                    `tools.pdf.${item.key}.title`,
                                                    item.title,
                                                ),
                                            )
                                        }}
                                    </Link>

                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.organize',
                                                'Organize PDF',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in organizePdfItems"
                                        :key="item.href"
                                        :href="item.href"
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.pdf.${item.key}.nav_title`,
                                                t(
                                                    `tools.pdf.${item.key}.title`,
                                                    item.title,
                                                ),
                                            )
                                        }}
                                    </Link>

                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.edit_pdf',
                                                'Edit PDF',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in editPdfItems"
                                        :key="item.href"
                                        :href="item.href"
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.pdf.${item.key}.nav_title`,
                                                t(
                                                    `tools.pdf.${item.key}.title`,
                                                    item.title,
                                                ),
                                            )
                                        }}
                                    </Link>

                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.security',
                                                'Security',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in securityPdfItems"
                                        :key="item.href"
                                        :href="item.href"
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.pdf.${item.key}.nav_title`,
                                                t(
                                                    `tools.pdf.${item.key}.title`,
                                                    item.title,
                                                ),
                                            )
                                        }}
                                    </Link>

                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.from_pdf',
                                                'Convert from PDF',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in convertFromPdfItems"
                                        :key="item.href"
                                        :href="item.href"
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.pdf.${item.key}.nav_title`,
                                                t(
                                                    `tools.pdf.${item.key}.title`,
                                                    item.title,
                                                ),
                                            )
                                        }}
                                    </Link>
                                </nav>
                                <nav v-else class="space-y-1 pb-6">
                                    <Link
                                        :href="dashboard.url()"
                                        prefetch
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                dashboard.url(),
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        {{ homeAriaLabel }}
                                    </Link>
                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.image_editor',
                                                'Image editor',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        :href="editorImageNavItem.href"
                                        prefetch
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                editorImageNavItem.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="editorImageNavItem.icon"
                                            :class="[
                                                'h-5 w-5',
                                                editorImageNavItem.iconClass,
                                            ]"
                                        />
                                        {{
                                            t(
                                                `tools.image.${editorImageNavItem.key}.title`,
                                                editorImageNavItem.title,
                                            )
                                        }}
                                    </Link>
                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.image_transform',
                                                'Transform',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in transformImageNavItems"
                                        :key="item.href"
                                        :href="item.href"
                                        prefetch
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.image.${item.key}.title`,
                                                item.title,
                                            )
                                        }}
                                    </Link>
                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.image_optimize',
                                                'Optimize',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in optimizeImageNavItems"
                                        :key="item.href"
                                        :href="item.href"
                                        prefetch
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.image.${item.key}.title`,
                                                item.title,
                                            )
                                        }}
                                    </Link>
                                    <p
                                        class="mt-4 mb-1 px-3 text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500"
                                    >
                                        {{
                                            t(
                                                'tools.sections.image_bulk',
                                                'Bulk',
                                            )
                                        }}
                                    </p>
                                    <Link
                                        v-for="item in bulkImageNavItems"
                                        :key="item.href"
                                        :href="item.href"
                                        prefetch
                                        class="flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium hover:bg-accent"
                                        :class="
                                            whenCurrentUrl(
                                                item.href,
                                                activeItemStyles,
                                            )
                                        "
                                    >
                                        <component
                                            :is="item.icon"
                                            :class="['h-5 w-5', item.iconClass]"
                                        />
                                        {{
                                            t(
                                                `tools.image.${item.key}.title`,
                                                item.title,
                                            )
                                        }}
                                    </Link>
                                </nav>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>

                <div class="flex h-full shrink-0 items-center">
                    <Link
                        :href="dashboard.url()"
                        prefetch
                        class="flex items-center gap-x-2 rounded-md ring-offset-background outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                        :aria-label="homeAriaLabel"
                        :aria-current="
                            isCurrentUrl(dashboard.url()) ? 'page' : undefined
                        "
                    >
                        <AppLogo />
                    </Link>
                </div>

                <!-- Desktop Menu -->
                <div
                    v-if="site === 'pdf'"
                    class="hidden min-w-0 flex-1 items-center justify-center lg:flex"
                >
                    <NavigationMenu class="flex h-full items-stretch">
                        <NavigationMenuList
                            class="flex h-full items-stretch space-x-2"
                        >
                            <!-- Convert to PDF dropdown -->
                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isPdfSectionActive(convertToPdfItems) &&
                                            activeItemStyles,
                                    ]"
                                >
                                    <FilePlus class="mr-2 h-4 w-4" />
                                    {{
                                        t(
                                            'tools.sections.to_pdf',
                                            'Convert to PDF',
                                        )
                                    }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul
                                        class="grid w-[420px] gap-1 p-2 md:grid-cols-2"
                                    >
                                        <li
                                            v-for="item in convertToPdfItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_title`,
                                                            t(
                                                                `tools.pdf.${item.key}.title`,
                                                                item.title,
                                                            ),
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="isPdfSectionActive(convertToPdfItems)"
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>

                            <!-- Organize PDF dropdown -->
                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isPdfSectionActive(organizePdfItems) &&
                                            activeItemStyles,
                                    ]"
                                >
                                    <Layers class="mr-2 h-4 w-4" />
                                    {{
                                        t(
                                            'tools.sections.organize',
                                            'Organize PDF',
                                        )
                                    }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul
                                        class="grid w-[420px] gap-1 p-2 md:grid-cols-2"
                                    >
                                        <li
                                            v-for="item in organizePdfItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_title`,
                                                            t(
                                                                `tools.pdf.${item.key}.title`,
                                                                item.title,
                                                            ),
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="isPdfSectionActive(organizePdfItems)"
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>

                            <!-- Edit PDF dropdown -->
                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isPdfSectionActive(editPdfItems) &&
                                            activeItemStyles,
                                    ]"
                                >
                                    <PencilLine class="mr-2 h-4 w-4" />
                                    {{
                                        t('tools.sections.edit_pdf', 'Edit PDF')
                                    }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul
                                        class="grid w-[420px] gap-1 p-2 md:grid-cols-2"
                                    >
                                        <li
                                            v-for="item in editPdfItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_title`,
                                                            t(
                                                                `tools.pdf.${item.key}.title`,
                                                                item.title,
                                                            ),
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="isPdfSectionActive(editPdfItems)"
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>

                            <!-- PDF Security dropdown -->
                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isPdfSectionActive(securityPdfItems) &&
                                            activeItemStyles,
                                    ]"
                                >
                                    <ShieldCheck class="mr-2 h-4 w-4" />
                                    {{
                                        t('tools.sections.security', 'Security')
                                    }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul
                                        class="grid w-[420px] gap-1 p-2 md:grid-cols-2"
                                    >
                                        <li
                                            v-for="item in securityPdfItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_title`,
                                                            t(
                                                                `tools.pdf.${item.key}.title`,
                                                                item.title,
                                                            ),
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="isPdfSectionActive(securityPdfItems)"
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>

                            <!-- Convert from PDF dropdown -->
                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isPdfSectionActive(
                                            convertFromPdfItems,
                                        ) && activeItemStyles,
                                    ]"
                                >
                                    <FileOutput class="mr-2 h-4 w-4" />
                                    {{
                                        t(
                                            'tools.sections.from_pdf',
                                            'Convert from PDF',
                                        )
                                    }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul
                                        class="grid w-[420px] gap-1 p-2 md:grid-cols-2"
                                    >
                                        <li
                                            v-for="item in convertFromPdfItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_title`,
                                                            t(
                                                                `tools.pdf.${item.key}.title`,
                                                                item.title,
                                                            ),
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.pdf.${item.key}.nav_description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="
                                        isPdfSectionActive(convertFromPdfItems)
                                    "
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>
                <div
                    v-else
                    class="hidden min-w-0 flex-1 items-center justify-center lg:flex"
                >
                    <NavigationMenu class="flex h-full items-stretch">
                        <NavigationMenuList
                            class="flex h-full items-stretch space-x-2"
                        >
                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <Link
                                    :href="editorImageNavItem.href"
                                    prefetch
                                    class="inline-flex h-9 items-center rounded-md px-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                                    :class="
                                        whenCurrentUrl(
                                            editorImageNavItem.href,
                                            activeItemStyles,
                                        )
                                    "
                                >
                                    <PencilLine class="mr-2 h-4 w-4" />
                                    {{
                                        t(
                                            `tools.image.${editorImageNavItem.key}.title`,
                                            editorImageNavItem.title,
                                        )
                                    }}
                                </Link>
                                <div
                                    v-if="isCurrentUrl(editorImageNavItem.href)"
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>

                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isImageSectionActive(
                                            transformImageNavItems,
                                        ) && activeItemStyles,
                                    ]"
                                >
                                    <Maximize2 class="mr-2 h-4 w-4" />
                                    {{
                                        t(
                                            'tools.sections.image_transform',
                                            'Transform',
                                        )
                                    }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul class="w-[320px] gap-1 p-2">
                                        <li
                                            v-for="item in transformImageNavItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                prefetch
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.image.${item.key}.title`,
                                                            item.title,
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.image.${item.key}.description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="
                                        isImageSectionActive(
                                            transformImageNavItems,
                                        )
                                    "
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>

                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isImageSectionActive(
                                            optimizeImageNavItems,
                                        ) && activeItemStyles,
                                    ]"
                                >
                                    <FilePlus class="mr-2 h-4 w-4" />
                                    {{
                                        t(
                                            'tools.sections.image_optimize',
                                            'Optimize',
                                        )
                                    }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul class="w-[320px] gap-1 p-2">
                                        <li
                                            v-for="item in optimizeImageNavItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                prefetch
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.image.${item.key}.title`,
                                                            item.title,
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.image.${item.key}.description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="
                                        isImageSectionActive(
                                            optimizeImageNavItems,
                                        )
                                    "
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>

                            <NavigationMenuItem
                                class="relative flex h-full items-center"
                            >
                                <NavigationMenuTrigger
                                    :class="[
                                        'h-9 cursor-pointer px-2',
                                        isImageSectionActive(
                                            bulkImageNavItems,
                                        ) && activeItemStyles,
                                    ]"
                                >
                                    <Layers class="mr-2 h-4 w-4" />
                                    {{ t('tools.sections.image_bulk', 'Bulk') }}
                                </NavigationMenuTrigger>
                                <NavigationMenuContent>
                                    <ul class="w-[320px] gap-1 p-2">
                                        <li
                                            v-for="item in bulkImageNavItems"
                                            :key="item.href"
                                        >
                                            <Link
                                                :href="item.href"
                                                prefetch
                                                class="block rounded-md p-3 leading-none no-underline transition-colors outline-none select-none hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                                                :class="
                                                    whenCurrentUrl(
                                                        item.href,
                                                        'bg-accent text-accent-foreground',
                                                    )
                                                "
                                            >
                                                <div
                                                    class="flex items-center gap-2 text-sm leading-none font-medium"
                                                >
                                                    <component
                                                        :is="item.icon"
                                                        :class="[
                                                            'h-4 w-4',
                                                            item.iconClass,
                                                        ]"
                                                    />
                                                    <span>{{
                                                        t(
                                                            `tools.image.${item.key}.title`,
                                                            item.title,
                                                        )
                                                    }}</span>
                                                </div>
                                                <p
                                                    class="mt-1 line-clamp-2 text-xs leading-snug text-muted-foreground"
                                                >
                                                    {{
                                                        t(
                                                            `tools.image.${item.key}.description`,
                                                            item.description,
                                                        )
                                                    }}
                                                </p>
                                            </Link>
                                        </li>
                                    </ul>
                                </NavigationMenuContent>
                                <div
                                    v-if="
                                        isImageSectionActive(bulkImageNavItems)
                                    "
                                    class="absolute bottom-0 left-0 h-0.5 w-full translate-y-px bg-black dark:bg-white"
                                />
                            </NavigationMenuItem>
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>

                <div class="ml-auto flex items-center space-x-2">
                    <ThemeToggle />
                    <DropdownMenu v-if="auth.user">
                        <DropdownMenuTrigger :as-child="true">
                            <Button
                                variant="ghost"
                                size="icon"
                                class="relative size-10 w-auto rounded-full p-1 focus-within:ring-2 focus-within:ring-primary"
                                :aria-label="
                                    t(
                                        'tools.nav.user_menu_aria',
                                        'Open account menu for {name}',
                                    ).replace('{name}', auth.user.name)
                                "
                            >
                                <Avatar
                                    class="size-8 overflow-hidden rounded-full"
                                >
                                    <AvatarImage
                                        v-if="auth.user.avatar"
                                        :src="auth.user.avatar"
                                        :alt="auth.user.name"
                                    />
                                    <AvatarFallback
                                        class="rounded-lg bg-neutral-200 font-semibold text-black dark:bg-neutral-700 dark:text-white"
                                    >
                                        {{ getInitials(auth.user?.name) }}
                                    </AvatarFallback>
                                </Avatar>
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" class="w-56">
                            <UserMenuContent :user="auth.user" />
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <template v-else-if="loginRegisterEnabled">
                        <Button
                            as-child
                            variant="ghost"
                            size="icon"
                            class="h-9 w-9 shrink-0"
                        >
                            <Link
                                :href="login.url()"
                                prefetch
                                class="inline-flex size-9 items-center justify-center rounded-md ring-offset-background outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                :aria-label="t('tools.nav.log_in', 'Log in')"
                                :title="t('tools.nav.log_in', 'Log in')"
                            >
                                <LogIn
                                    class="size-5 opacity-85 hover:opacity-100"
                                />
                            </Link>
                        </Button>
                    </template>
                </div>
            </div>
        </div>

        <div
            v-if="props.breadcrumbs.length > 1"
            class="flex w-full border-b border-sidebar-border/70"
        >
            <div
                class="mx-auto flex h-12 w-full items-center justify-start px-4 text-neutral-500 md:max-w-7xl"
            >
                <Breadcrumbs :breadcrumbs="breadcrumbs" />
            </div>
        </div>
    </div>
</template>
